import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, MEDIA } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const handleMouseMove = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  card.style.setProperty("--x", `${e.clientX - rect.left}px`);
  card.style.setProperty("--y", `${e.clientY - rect.top}px`);
};

export const ServicesBento = () => {
  const items = SERVICES.slice(0, 6);

  return (
    <section data-testid="services-bento" className="relative py-24 md:py-32 bg-[#FAF7F2]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                01 — What we do
              </div>
              <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl">
                A full-stack consulting house.<br />
                <span className="text-stone-500">From people ops to platform engineering.</span>
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-bento-cta"
              className="inline-flex items-center gap-2 text-[#B8860B] hover:text-[#B8860B] link-underline"
            >
              All capabilities
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Featured hero card (col-span 7, row-span 2) */}
          <Reveal className="md:col-span-7 md:row-span-2 bento-card group" onMouseMove={handleMouseMove}>
            <Link to="/services#ai-ml" data-testid="service-card-ai-ml" className="block relative h-full min-h-[460px]">
              <img src={MEDIA.aiTech} alt="AI & ML" className="absolute inset-0 w-full h-full object-cover opacity-95 pencil-soft" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-white/10" />
              <div className="relative h-full p-10 flex flex-col justify-end">
                <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] mb-3">
                  Flagship · AI & ML
                </div>
                <h3 className="font-display text-3xl md:text-5xl text-stone-900 font-light tracking-tighter mb-4 max-w-lg">
                  Applied intelligence, measurable outcomes.
                </h3>
                <p className="text-stone-700 max-w-md mb-6">
                  GenAI copilots, RAG systems and forecasting moved from proof-of-concept to governed production.
                </p>
                <div className="inline-flex items-center gap-2 text-stone-900 group-hover:text-[#B8860B] transition-colors">
                  Learn more <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </div>
            </Link>
          </Reveal>

          {items.slice(0, 2).map((s) => (
            <Reveal key={s.id} className="md:col-span-5 bento-card group" onMouseMove={handleMouseMove}>
              <Link to={`/services#${s.id}`} data-testid={`service-card-${s.id}`} className="block p-8 h-full">
                <div className="flex items-start justify-between mb-8">
                  <s.icon className="w-8 h-8 text-[#B8860B]" strokeWidth={1.25} />
                  <ArrowUpRight className="w-5 h-5 text-stone-500 group-hover:text-[#B8860B] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-stone-900 font-light tracking-tight mb-3">
                  {s.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">{s.description}</p>
              </Link>
            </Reveal>
          ))}

          {items.slice(2, 6).map((s) => (
            <Reveal key={s.id} className="md:col-span-3 bento-card group" onMouseMove={handleMouseMove}>
              <Link to={`/services#${s.id}`} data-testid={`service-card-${s.id}`} className="block p-8 h-full">
                <s.icon className="w-7 h-7 text-[#B8860B] mb-8" strokeWidth={1.25} />
                <h3 className="font-display text-xl text-stone-900 font-medium mb-2">{s.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{s.tagline}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-xs text-stone-600 group-hover:text-[#B8860B] transition-colors uppercase tracking-[0.2em]">
                  Explore <ArrowUpRight className="w-3 h-3" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesBento;
